import type { PostList, RightsList, Wall, Workspace } from "@brandwall/contracts";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link, Navigate, NavLink, Outlet, useLocation, useParams } from "react-router";
import { useMe } from "../App";
import { api, post } from "../api";
import { Icon } from "./Icons";
import { ToastProvider } from "./Toast";

/** Pages that exist so far. */
const READY = new Set([
  "",
  "inbox",
  "discover",
  "rights",
  "walls",
  "products",
  "sources",
  "taste",
  "seasons",
  "ai",
  "insights",
  "settings",
]);

export function useWorkspace(): Workspace {
  const { ws } = useParams();
  const me = useMe();
  return me.data!.workspaces.find((w) => w.id === ws)!;
}

export function Shell() {
  const { ws } = useParams();
  const me = useMe();
  const location = useLocation();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const workspace = me.data?.workspaces.find((w) => w.id === ws);
  const counts = useQuery({
    queryKey: ["counts", ws, "posts"],
    queryFn: () => api<PostList>(`/v1/w/${ws}/posts?tab=to_review&limit=1`),
    enabled: Boolean(workspace),
    // New posts arrive in the background, so the numbers check again every minute.
    refetchInterval: 60_000,
  });
  const rights = useQuery({
    queryKey: ["counts", ws, "rights"],
    queryFn: () => api<RightsList>(`/v1/w/${ws}/rights`),
    enabled: Boolean(workspace),
    refetchInterval: 60_000,
  });
  const walls = useQuery({
    queryKey: ["walls", ws],
    queryFn: () => api<{ walls: Wall[] }>(`/v1/w/${ws}/walls`),
    enabled: Boolean(workspace),
  });
  if (!workspace) return <Navigate to="/" replace />;
  const user = me.data!.user;
  const toReview = counts.data?.counts.to_review ?? 0;
  const discover = counts.data?.counts.discover ?? 0;
  const waiting =
    (rights.data?.counts.pending ?? 0) +
    (rights.data?.counts.replied ?? 0) +
    (rights.data?.counts.draft ?? 0);
  const main = walls.data?.walls.find((w) => w.status === "live" && w.kind === "normal");
  const titles: Record<string, [string, string?]> = {
    "": ["Home"],
    inbox: ["Inbox"],
    discover: ["Discover"],
    rights: ["Rights", "Permission to show posts"],
    walls: ["Walls"],
    products: ["Products"],
    seasons: ["Seasons"],
    sources: ["Sources"],
    taste: ["Taste"],
    ai: ["AI & MCP"],
    insights: ["Insights"],
    settings: ["Settings"],
  };
  const nav: [string, string, React.ReactNode, number?][] = [
    ["", "Home", Icon.home],
    ["inbox", "Inbox", Icon.inbox, toReview],
    ["discover", "Discover", Icon.discover, discover],
    ["rights", "Rights", Icon.rights, waiting],
    ["walls", "Walls", Icon.walls],
    ["products", "Products", Icon.products],
    ["seasons", "Seasons", Icon.seasons],
    ["sources", "Sources", Icon.sources],
    ["taste", "Taste", Icon.taste],
    ["ai", "AI & MCP", Icon.ai],
    ["insights", "Insights", Icon.insights],
    ["settings", "Settings", Icon.settings],
  ];
  const section = location.pathname.split("/")[3] ?? "";

  const logout = async () => {
    await post("/auth/logout");
    qc.clear();
    window.location.href = "/login";
  };

  return (
    <ToastProvider>
      <div className="app">
        <nav className={`side ${open ? "open" : ""}`} aria-label="Main">
          <img className="logo" src="/logo.svg" alt="Brandwall" />
          <div className="ws">
            <div className="sq">
              {workspace.brand.logoUrl ? (
                <img src={workspace.brand.logoUrl} alt="" />
              ) : (
                workspace.name[0]?.toUpperCase()
              )}
            </div>
            <b title={workspace.name}>{workspace.name}</b>
            <span>{workspace.plan}</span>
          </div>
          <div className="navs">
            {nav
              .filter(([path]) => READY.has(path))
              .map(([path, label, icon, badge]) => (
                <NavLink
                  key={path}
                  end={path === ""}
                  to={path ? `/w/${ws}/${path}` : `/w/${ws}`}
                  className="nav"
                  onClick={() => setOpen(false)}
                >
                  {icon} {label} {badge ? <span className="bd">{badge}</span> : null}
                </NavLink>
              ))}
          </div>
          <div className="me">
            <div className="av">
              {user.avatarUrl ? (
                <img src={user.avatarUrl} alt="" />
              ) : (
                (user.name ?? user.email)[0]?.toUpperCase()
              )}
            </div>
            <div style={{ minWidth: 0 }}>
              <b>{user.name ?? user.email}</b>
              {user.admin && (
                <Link to="/admin" className="small" style={{ display: "block" }}>
                  Super admin
                </Link>
              )}
              <button type="button" className="btn link small" onClick={logout}>
                Log out
              </button>
            </div>
          </div>
        </nav>
        <div className="main">
          <header className="top">
            <button
              type="button"
              className="btn secondary sm menu-btn"
              aria-label="Open menu"
              onClick={() => setOpen(!open)}
            >
              {Icon.menu}
            </button>
            <h1>{titles[section]?.[0] ?? "Brandwall"}</h1>
            {titles[section]?.[1] && <span className="crumb hide-sm">{titles[section]?.[1]}</span>}
            <span className="spacer" />
            {main && (
              <NavLink to={`/w/${ws}/walls/${main.id}`} className="pill hide-sm">
                <span className="dot" /> {main.name} · Live
              </NavLink>
            )}
          </header>
          <main className="content">
            <Outlet />
          </main>
        </div>
      </div>
    </ToastProvider>
  );
}
